import React from 'react';
import { Topic, TopicStatus } from '../types';
import { LockIcon, PlayIcon, CheckCircleIcon, RobotIcon, DocumentTextIcon, VideoCameraIcon, PresentationChartBarIcon } from './icons';

interface TopicCardProps {
  topic: Topic;
  status: TopicStatus;
  onSelect: (topic: Topic) => void;
}

const ContentIcon = ({ contentType, className }: { contentType: Topic['contentType'], className?: string }) => {
  switch (contentType) {
    case 'pdf': return <DocumentTextIcon className={className} />;
    case 'video': return <VideoCameraIcon className={className} />;
    case 'presentation': return <PresentationChartBarIcon className={className} />;
    default: return null;
  }
};

const TopicCard: React.FC<TopicCardProps> = ({ topic, status, onSelect }) => {
  const isLocked = status === TopicStatus.LOCKED;
  const isCompleted = status === TopicStatus.COMPLETED;
  const isInProgress = status === TopicStatus.IN_PROGRESS;

  const handleClick = () => {
    if (isLocked) return;
    onSelect(topic);
  };

  return (
    <div
      onClick={handleClick}
      className={`flex-shrink-0 w-64 md:w-72 aspect-video rounded-lg overflow-hidden relative shadow-lg group transition-all duration-300 transform-gpu 
        ${isLocked ? 'cursor-not-allowed' : 'cursor-pointer hover:scale-105 hover:shadow-2xl hover:z-10'}`}
    >
      {/* Background and Icon */}
      <div className="absolute inset-0 bg-slate-800 flex items-center justify-center">
        <ContentIcon contentType={topic.contentType} className="w-16 h-16 text-slate-600" />
      </div>
      
      {/* Content overlay on hover */}
      {!isLocked && (
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 p-4 flex flex-col justify-between">
           <div>
             <h3 className="text-white font-bold text-lg">{topic.title}</h3>
             <p className="text-gray-300 text-xs mt-1 line-clamp-2">{topic.description}</p>
           </div>
           <div className="flex items-center justify-center">
             <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-white">
                <PlayIcon className="w-8 h-8 text-white" />
             </div>
           </div>
           <div></div>
        </div>
      )}

      {/* Title bar at bottom */}
      <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
        <h3 className="text-white font-semibold truncate group-hover:opacity-0 transition-opacity">{topic.title}</h3>
      </div>
      
      {/* Locked Overlay */}
      {isLocked && (
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center text-center p-4">
          <LockIcon className="w-10 h-10 text-gray-400 mb-2" />
          <p className="text-gray-300 font-semibold">Bloqueado</p>
        </div>
      )}

      {/* Completed Checkmark */}
      {isCompleted && (
        <div className="absolute top-2 right-2 w-7 h-7 bg-green-500 rounded-full flex items-center justify-center shadow-md">
          <CheckCircleIcon className="w-5 h-5 text-white" />
        </div>
      )}

      {/* Progress Bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-slate-700">
        <div
          className="h-full bg-blue-500 transition-all duration-500"
          style={{ width: isCompleted ? '100%' : isInProgress ? '50%' : '0%' }}
        />
      </div>
    </div>
  );
};

export default TopicCard;